/**
 * A gaveta do carrinho.
 *
 * O QUE ISTO RESOLVE
 * ------------------
 * O ícone do carrinho levava o cliente para /cart. Com o carrinho vazio isso
 * dava um ecrã inteiro em branco com um cesto pequeno ao meio — o cliente
 * perdia a página onde estava e não recebia nada em troca.
 *
 * Agora o ícone abre um painel por cima, à direita. A página por trás fica
 * onde estava. Fecha-se no X, no fundo escurecido, ou no Esc.
 *
 * A REGRA DESTE FICHEIRO
 * ----------------------
 * O ecrã é SEMPRE desenhado com o que o servidor respondeu. Nunca há contas
 * feitas aqui: se o servidor recusar uma quantidade, o painel mostra a verdade
 * dele e não a nossa. É a mesma regra do carrinho.bundle.js ao lado.
 *
 * OS DOIS CARRINHOS
 * -----------------
 * Ler é igual para toda a gente: `cybershop.carrinho.api.resumo` já trata de
 * saber se há um cesto de visitante ou um orçamento de quem tem conta.
 * Escrever é que muda, porque são documentos diferentes — ver `guardar()`.
 */
(function () {
	"use strict";

	const LEITURA = "cybershop.carrinho.api.resumo";
	const ESCRITA_VISITANTE = "cybershop.carrinho.api.actualizar";
	const ESCRITA_COM_CONTA = "webshop.webshop.shopping_cart.cart.update_cart";

	let gaveta, fundo, corpo, rodape, contagem, totalTexto;
	let aberta = false;
	let ocupado = false;
	let focoAnterior = null;

	function eVisitante() {
		return !window.frappe || !frappe.session || frappe.session.user === "Guest";
	}

	/** Uma chamada ao servidor, já como promessa NATIVA.
	 *
	 *  O `Promise.resolve()` não é decoração. O `frappe.call` devolve uma
	 *  promessa do jQuery, que **não tem `.finally`** — só `.always`. Sem esta
	 *  conversão, qualquer cadeia terminada em `.finally` rebentava com
	 *  «.finally is not a function», e rebentava DEPOIS de o pedido ter
	 *  corrido bem: o servidor gravava, o ecrã actualizava, e só a limpeza
	 *  final é que ficava por fazer. Um defeito que não se vê ao testar uma
	 *  vez, porque a primeira acção parece funcionar.
	 */
	function chamar(metodo, args) {
		return Promise.resolve(
			frappe.call({ method: metodo, args: args || {}, type: "POST" })
		).then((r) => r.message);
	}

	function escapar(texto) {
		const div = document.createElement("div");
		div.textContent = texto == null ? "" : String(texto);
		return div.innerHTML;
	}

	// ------------------------------------------------------------------ abrir

	function abrir() {
		if (!gaveta || aberta) return;
		focoAnterior = document.activeElement;
		aberta = true;
		fundo.hidden = false;
		gaveta.hidden = false;
		// Um quadro de espera antes de acrescentar a classe: sem isto o
		// browser aplica o estado final e o painel aparece de repente, sem
		// deslizar. Ele precisa de pintar o estado inicial primeiro.
		requestAnimationFrame(() => {
			fundo.classList.add("aberta");
			gaveta.classList.add("aberta");
		});
		document.body.classList.add("ct-sem-scroll");
		const fechar = gaveta.querySelector("[data-ct-gaveta-fechar]");
		if (fechar) fechar.focus();
		carregar();
	}

	function fechar() {
		if (!gaveta || !aberta) return;
		aberta = false;
		fundo.classList.remove("aberta");
		gaveta.classList.remove("aberta");
		document.body.classList.remove("ct-sem-scroll");
		// Esconder só no fim da animação, senão o painel desaparece de golpe.
		setTimeout(() => {
			if (aberta) return;
			gaveta.hidden = true;
			fundo.hidden = true;
		}, 220);
		if (focoAnterior && focoAnterior.focus) focoAnterior.focus();
	}

	// --------------------------------------------------------------- desenhar

	function aCarregar() {
		corpo.innerHTML = '<p class="ct-gaveta-espera">A carregar…</p>';
		rodape.hidden = true;
	}

	function desenharVazio() {
		contagem.textContent = "";
		rodape.hidden = true;
		corpo.innerHTML =
			'<div class="ct-gaveta-vazio">' +
			'<svg class="ct-gaveta-icone" viewBox="0 0 24 24" aria-hidden="true">' +
			'<path d="M6.5 8h11l1 12h-13z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg>' +
			"<p>" + escapar(__("O seu carrinho está vazio")) + "</p>" +
			'<a class="ct-botao-principal" href="/all-products">' +
			escapar(__("Ver os produtos")) + "</a>" +
			"</div>";
	}

	function linhaHtml(artigo) {
		const rota = artigo.rota ? "/" + artigo.rota : "#";
		const imagem = artigo.imagem
			? '<img src="' + escapar(artigo.imagem) + '" alt="" loading="lazy">'
			: '<span class="ct-gaveta-sem-imagem"></span>';

		return (
			'<article class="ct-gaveta-linha" data-item-code="' +
			escapar(artigo.item_code) + '">' +
			'<a class="ct-gaveta-imagem" href="' + escapar(rota) + '">' + imagem + "</a>" +
			'<div class="ct-gaveta-texto">' +
			'<a class="ct-gaveta-nome" href="' + escapar(rota) + '">' +
			escapar(artigo.nome) + "</a>" +
			'<p class="ct-gaveta-preco">' + escapar(artigo.preco_formatado) + "</p>" +
			'<div class="ct-gaveta-qtd">' +
			'<button type="button" class="ct-qtd-botao" data-passo="-1" aria-label="' +
			escapar(__("Menos um")) + '">–</button>' +
			'<span class="ct-gaveta-qtd-valor">' + escapar(Math.round(artigo.qty)) + "</span>" +
			'<button type="button" class="ct-qtd-botao" data-passo="1" aria-label="' +
			escapar(__("Mais um")) + '">+</button>' +
			"</div></div>" +
			'<div class="ct-gaveta-lado">' +
			'<button type="button" class="ct-gaveta-remover" aria-label="' +
			escapar(__("Remover")) + '">' +
			'<svg viewBox="0 0 24 24" aria-hidden="true">' +
			'<path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12"/></svg></button>' +
			'<div class="ct-gaveta-valor">' + escapar(artigo.valor_formatado) + "</div>" +
			"</div></article>"
		);
	}

	function desenhar(dados) {
		if (!dados || !dados.artigos || !dados.artigos.length) {
			desenharVazio();
			actualizarContador(0);
			return;
		}

		const n = Math.round(dados.total_qtd);
		contagem.textContent = n + " " + (n === 1 ? __("produto") : __("produtos"));
		corpo.innerHTML = dados.artigos.map(linhaHtml).join("");
		totalTexto.textContent = dados.total_formatado || "";
		rodape.hidden = false;
		actualizarContador(dados.total_qtd);
	}

	/** O número no ícone. O carrinho.bundle.js tem a sua cópia disto; aqui
	 *  fica a versão curta, porque a gaveta também muda quantidades. */
	function actualizarContador(n) {
		const alvo = document.querySelector("[data-ct-contador]");
		if (!alvo) return;
		const quantos = Math.round(n) || 0;
		alvo.textContent = quantos;
		alvo.hidden = quantos <= 0;
	}

	// ---------------------------------------------------------------- servidor

	function carregar() {
		aCarregar();
		chamar(LEITURA)
			.then(desenhar)
			.catch(() => {
				corpo.innerHTML =
					'<p class="ct-gaveta-espera">' +
					escapar(__("Não foi possível abrir o carrinho.")) +
					"</p>";
			});
	}

	/** Grava uma quantidade nova e redesenha com a resposta do servidor.
	 *
	 *  Zero remove. São dois destinos porque são dois documentos: o cesto do
	 *  visitante e o orçamento de quem tem conta. Para o segundo chama-se a
	 *  API do próprio webshop, que já sabe recalcular impostos e totais — não
	 *  se reescreve isso aqui. */
	function guardar(itemCode, qtd) {
		if (ocupado) return;
		ocupado = true;
		gaveta.classList.add("ct-gaveta-a-mudar");

		const pedido = eVisitante()
			? chamar(ESCRITA_VISITANTE, { item_code: itemCode, qty: qtd })
			: chamar(ESCRITA_COM_CONTA, { item_code: itemCode, qty: qtd });

		pedido
			// A resposta de cada carrinho tem forma própria; voltamos sempre a
			// perguntar o resumo para desenhar a partir de uma só verdade.
			.then(() => chamar(LEITURA))
			.then(desenhar)
			.catch(() => {
				if (window.frappe && frappe.show_alert) {
					frappe.show_alert(
						{ message: __("Não foi possível actualizar."), indicator: "red" },
						3
					);
				}
				carregar();
			})
			.finally(() => {
				ocupado = false;
				gaveta.classList.remove("ct-gaveta-a-mudar");
			});
	}

	// ----------------------------------------------------------------- ligações

	function ligar() {
		gaveta = document.querySelector("[data-ct-gaveta]");
		fundo = document.querySelector("[data-ct-gaveta-fundo]");
		if (!gaveta || !fundo) return;

		corpo = gaveta.querySelector("[data-ct-gaveta-corpo]");
		rodape = gaveta.querySelector("[data-ct-gaveta-rodape]");
		contagem = gaveta.querySelector("[data-ct-gaveta-contagem]");
		totalTexto = gaveta.querySelector("[data-ct-gaveta-total]");

		// QUALQUER ligação para o carrinho abre a gaveta: o ícone do
		// cabeçalho, o «Carrinho» do rodapé, e o que vier a existir. Apanha-se
		// pelo destino e não por uma classe, senão cada sítio novo que aponte
		// para /cart voltava a levar a pessoa para fora da página — e ninguém
		// se ia lembrar porquê.
		//
		// O href continua lá de propósito. É o caminho de quem tem o
		// JavaScript desligado e de quem abre em separador novo (daí os
		// modificadores serem respeitados) — para esses, o /cart funciona
		// como sempre funcionou.
		document.addEventListener("click", function (e) {
			const ligacao = e.target.closest('a[href="/cart"], a[href$="/cart"], .ct-acao-carrinho');
			if (!ligacao) return;
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
			if (ligacao.target === "_blank") return;

			// Dentro da própria gaveta há um botão «Ver o carrinho» que TEM de
			// navegar: é ele que leva ao passo «Onde quer receber?».
			if (ligacao.closest("[data-ct-gaveta]")) return;

			e.preventDefault();
			abrir();
		});

		fundo.addEventListener("click", fechar);
		gaveta.addEventListener("click", function (e) {
			if (e.target.closest("[data-ct-gaveta-fechar]")) {
				fechar();
				return;
			}

			const passo = e.target.closest(".ct-qtd-botao");
			if (passo) {
				const linha = passo.closest(".ct-gaveta-linha");
				const actual = parseInt(
					linha.querySelector(".ct-gaveta-qtd-valor").textContent,
					10
				);
				const nova = actual + parseInt(passo.dataset.passo, 10);
				// Não se desce a zero pelo «–»: para remover há o caixote, que
				// é um gesto explícito. Chegar a zero sem querer apagava o
				// artigo sem o cliente perceber.
				if (nova >= 1) guardar(linha.dataset.itemCode, nova);
				return;
			}

			const remover = e.target.closest(".ct-gaveta-remover");
			if (remover) {
				guardar(remover.closest(".ct-gaveta-linha").dataset.itemCode, 0);
			}
		});

		document.addEventListener("keydown", function (e) {
			if (e.key === "Escape" && aberta) fechar();
		});

		// Acrescentar um artigo abre a gaveta: o cliente vê o carrinho a
		// encher, em vez de um aviso que desaparece. Para quem tem conta é o
		// JavaScript do webshop que grava, e ele demora — daí a espera antes
		// de ler. Para quem não tem conta, o carrinho.bundle.js avisa-nos
		// assim que o servidor responde (evento ct:carrinho-mudou).
		document.addEventListener("click", function (e) {
			if (!e.target.closest(".btn-add-to-cart-list, .btn-add-to-cart")) return;
			if (eVisitante()) return;
			abrir();
			setTimeout(carregar, 900);
		});

		// `abrir()` já lê o carrinho; se a gaveta estiver aberta basta reler.
		document.addEventListener("ct:carrinho-mudou", function () {
			if (aberta) carregar();
			else abrir();
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", ligar);
	} else {
		ligar();
	}
})();
